import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { TaxTableModalModel } from 'src/app/models/tables/wallet/tax-table-modal.model';
import { environment } from './../../../environments/environment';
import { enpointCervinoWeb } from './../../configuration/enpointsCervino';
import { TaxModel } from './../../models/management/tax.model';
import { TaxImportModel } from 'src/app/models/imports/tax-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';

@Injectable({
  providedIn: 'root'
})
export class TaxService {

  private readonly _httpClient = inject(HttpClient);

  createTax(taxtModel: TaxModel): Observable<TaxModel> {
    return this._httpClient.post<TaxModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.CreateTax}`,
      taxtModel);
  }

  deleteTax(taxId: number): Observable<TaxModel> {
    return this._httpClient.delete<TaxModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.DeleteTax}/${taxId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.tax.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.ExportImportErrors}`, alerts,
      { responseType: 'blob' }
    );
  }

  getAllTaxesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetAllTaxesWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllTypesForSelect(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetAllTypesForSelectAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForTaxes(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetColumnsForTaxesAsync}`);
  }

  getIvaTaxes(): Observable<Array<TaxModel>> {
    return this._httpClient.get<Array<TaxModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetIvaTaxes}`);
  }

  getRtfTaxes(): Observable<Array<TaxModel>> {
    return this._httpClient.get<Array<TaxModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetRtfTaxes}`);
  }

  getTaxAndTypeById(accountingInterfaceId: number): Observable<{ tax: string, type: string, taxTypeId: number, minimumBase: number, rate: number }> {
    return this._httpClient.get<{ tax: string, type: string, taxTypeId: number, minimumBase: number, rate: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetTaxAndTypeByIdAsync}/${accountingInterfaceId}`);
  }

  getTaxByCode(taxTypeId: number, code: string): Observable<TaxModel> {
    const itemFilterResponse = new ItemFilterModel("code", taxTypeId, null, "");
    itemFilterResponse.wildcard = code;
    return this._httpClient.post<TaxModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetTaxByCode}`, itemFilterResponse)
  }

  getTaxById(taxId: number): Observable<TaxModel> {
    return this._httpClient.get<TaxModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetTaxById}/${taxId}`);
  }

  getTaxesForModal(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<TaxTableModalModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<TaxTableModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.GetTaxesForModalAsync}`,
      filters, { params: params }
    );
  }

  importTaxes(taxesImports: Array<TaxImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.ImportTaxesAsync}`,
      taxesImports
    );
  }

  updateTax(taxtModel: TaxModel): Observable<TaxModel> {
    return this._httpClient.put<TaxModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.tax.UpdateTax}`,
      taxtModel);
  }
}
